"use client";

import { ArrowRight } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { FormEvent, Suspense, useState } from "react";

import { VisiaLogo } from "@/components/shared/icons";
import { Spinner } from "@/components/ui/spinner";

function Login() {
  const searchParams = useSearchParams();
  const invitation = searchParams.get("invitation");
  const invitedOrganization = searchParams.get("organization");
  const isDemoLogin =
    searchParams.get("demo") === "1" ||
    searchParams.get("organization_name") === "visia-deployment-demo";
  const [email, setEmail] = useState("");
  // Set by onCallback in src/lib/auth0.ts when the Auth0 callback fails.
  const [error, setError] = useState(() =>
    searchParams.get("auth_error")
      ? searchParams.get("auth_error_description") ||
        "We couldn't sign you in. Please try again, or contact support@visia.ai."
      : "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signIn = async (connection?: string) => {
    setError("");

    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setIsSubmitting(true);
      let organization = invitedOrganization;
      if (!invitation || !organization) {
        const lookupUrl = new URL(
          `/api/org-by-email/${encodeURIComponent(email)}`,
          window.location.origin,
        );
        if (isDemoLogin) lookupUrl.searchParams.set("demo", "1");
        const response = await fetch(lookupUrl);
        const body = await response.json();

        if (!response.ok) {
          setError(body.message || "Login failed. Please try again.");
          setIsSubmitting(false);
          return;
        }
        organization = body.organization;
      }
      if (!organization) {
        setError("No organization found for this account.");
        setIsSubmitting(false);
        return;
      }

      const authUrl = new URL("/auth/login", window.location.origin);
      authUrl.searchParams.set("organization", organization);
      authUrl.searchParams.set("login_hint", email);
      if (connection) authUrl.searchParams.set("connection", connection);
      if (invitation) authUrl.searchParams.set("invitation", invitation);

      const returnTo = new URLSearchParams(window.location.search).get(
        "returnTo",
      );
      if (returnTo) {
        authUrl.searchParams.set("returnTo", returnTo);
      }

      window.location.href = authUrl.toString();
    } catch {
      setError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void signIn();
  };

  // V2 Solstice – Login (67:1994): flat slate page, centered SOLSTICE wordmark
  // over a single rounded email input with an inline mint arrow button. No
  // card chrome, no Welcome heading — just the brand and a single CTA.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div
        className="flex w-full max-w-[400px] flex-col items-center"
        data-component="login-card"
      >
        <div className="flex items-center gap-3">
          <VisiaLogo size={42} />
          <span className="text-[40px] font-extrabold tracking-[0.04em] text-foreground">
            SOLSTICE
          </span>
        </div>

        {isDemoLogin ? (
          <p className="mt-3 text-body-lg font-medium text-muted-foreground">
            Visia Deployment Demo
          </p>
        ) : null}

        <form
          onSubmit={handleSubmit}
          className="mt-15 flex w-full items-center"
        >
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <div className="relative flex w-full items-center">
            <input
              id="email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              className="h-[48px] w-full rounded-[10px] border border-border bg-card pl-5 pr-14.5 text-body-lg font-medium text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              aria-label="Continue"
              className="absolute right-1.5 flex h-[36px] w-[44px] items-center justify-center rounded-[6px] bg-accent text-accent-foreground transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
            >
              {isSubmitting ? (
                <Spinner size={16} />
              ) : (
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              )}
            </button>
          </div>
        </form>

        {error && (
          <p className="mt-3 self-start text-caption text-destructive">
            {error}
          </p>
        )}

        {!isDemoLogin ? (
          <div className="my-6 flex w-full items-center gap-3 text-caption text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            or
            <div className="h-px flex-1 bg-border" />
          </div>
        ) : null}

        {!isDemoLogin ? (
          <button
            type="button"
            onClick={() => void signIn("microsoft")}
            disabled={isSubmitting}
            className="flex h-[48px] w-full items-center justify-center gap-3 rounded-[10px] border border-border bg-card text-body-lg font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Continue with Microsoft
          </button>
        ) : null}
      </div>

      <p className="absolute bottom-8 text-body text-muted-foreground">
        Built by Visia
      </p>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <Login />
    </Suspense>
  );
}
